import { ReactNode } from 'react';
import { Field, FieldState, useFormState } from '@enterprise/common';
import { SpotSelectBox, SpotSelectOption, SpotTextInput } from '@enterprise/spot';
import { observer } from 'mobx-react';
import { useStores } from '../../../../../hooks';
import { notEmpty } from '../../../../../core/helpers';
import { Trans } from 'react-i18next';
import { LookupItemTypes } from '../../../../../core/models/datasource/lookups';
import i18n from 'i18next';
import { InvoiceItemWithVariantDetail } from '../../../../../core/models';

interface HeaderControlProps {
    areFieldsDisabled?: boolean;
    queryPractice?: string;
    copyDescriptionToAll?: (description: string) => void;
    isNotExist?: (value: string, meta: FieldState<unknown> | undefined, message: string) => ReactNode | Promise<ReactNode | undefined>;
}

export const HeaderControl = observer(function HeaderControl(props: HeaderControlProps) {
    const {
        domain: { itemsPageStore: store },
    } = useStores();

    const { areFieldsDisabled, queryPractice, copyDescriptionToAll, isNotExist } = props;
    const requiredIndicator = '* ';
    const { isCreatingNewItem } = store;
    const itemTypes: LookupItemTypes[] = store.getLookupResults(store.itemTypes);
    const pimsIdPlaceholder = i18n.t('controlCenter:itemFields.code', 'Code');
    const itemTypePlaceholder = requiredIndicator + i18n.t('common:selectValue', 'Select a value');
    const hospitalDescriptionPlaceholder = requiredIndicator + i18n.t('controlCenter:itemFields.name', 'Name');
    const itemDescriptionAlreadyInUse = i18n.t('validation:itemDescriptionAlreadyInUse', 'Item Description already in use');
    const { values } = useFormState<InvoiceItemWithVariantDetail>();
    const { itemParent } = values;
    const isSubproduct = Boolean(itemParent?.pimsGeneratedId);

    const toUpperCase = (value) => {
        return value ? value.toUpperCase() : value;
    };

    return (
        <>
            <Field
                name="pimsId"
                parse={(value) => {
                    if (!value) {
                        return null;
                    }

                    return isCreatingNewItem ? toUpperCase(value) : value;
                }}
            >
                {({ input, meta }) => <SpotTextInput {...input} error={meta} placeholder={pimsIdPlaceholder} />}
            </Field>
            <Field name="itemType.id" parse={(value) => value && Number(value)} validate={notEmpty('Item Type')}>
                {({ input, meta }) => (
                    <SpotSelectBox {...input} error={meta} disabled={isSubproduct} placeholder={itemTypePlaceholder}>
                        {itemTypes.map((type) => (
                            <SpotSelectOption key={type.id} value={type.id}>
                                {type.description}
                            </SpotSelectOption>
                        ))}
                    </SpotSelectBox>
                )}
            </Field>
            <Field
                name="hospitalDescription"
                validate={(val, _, meta?) =>
                    notEmpty('Name')(val) ||
                    (!new RegExp('^[a-zA-Z0-9].*').test(val) &&
                        'The first character of the hospital description must be one of the following characters. 0-9 or A-Z or a-z') ||
                    (isCreatingNewItem && isNotExist?.(val, meta, itemDescriptionAlreadyInUse))
                }
            >
                {({ input, meta }) => {
                    const isCopyDescriptionLinkVisible =
                        !areFieldsDisabled && input.value && !(meta && meta.error && meta.touched) && !Boolean(queryPractice);
                    const copyHandler = () => copyDescriptionToAll?.(input.value);
                    return (
                        <div>
                            <SpotTextInput {...input} error={meta} placeholder={hospitalDescriptionPlaceholder} />
                            {isCopyDescriptionLinkVisible && (
                                <span className="spot-link copy-description-link" onClick={copyHandler}>
                                    <Trans i18nKey="common:item.applyDescriptionToAllVariants" />
                                </span>
                            )}
                        </div>
                    );
                }}
            </Field>
        </>
    );
});
